<script setup lang="ts">
import { injectSelectGroupContext, SelectItem, SelectItemIndicator, SelectItemText } from 'reka-ui';
import { computed, useId } from 'vue';

import { useDataTestid } from '@/kasten-components/composables';
import { cnMerge } from '@/utils/utils.js';
import IconCheck from '~icons/lucide/check';

import { injectSelectContext } from './context';
import KaSelectIconSlot from './KaSelectIconSlot.vue';
import { selectOptionVariants } from './select-variants';
import type { KaSelectOptionProps } from './types';

export type { KaSelectOptionProps } from './types';

const props = defineProps<KaSelectOptionProps>();
// Slot return types are `any` on purpose: Vue ignores a `defineSlots` entry's return type (only the
// slot props are checked), and `VNode | VNode[]` makes vue-tsc recursively expand VNode's
// self-referential children type until it trips TS2589 ("Type instantiation is excessively deep").
// `any` is Vue's documented convention.
const slots = defineSlots<{
  default?(): any;
  description?(props: { description?: string }): any;
  left?(): any;
  right?(): any;
}>();

const option = computed(() => ({ ...props }));

const selectContext = injectSelectContext();
const selectDataTestid = computed(() => selectContext?.dataTestid?.value);

// Pass a computed (not a one-time string) so the option's test id tracks runtime changes to the
// parent select's dataTestid/id, keeping the deliberately-reactive injected context intact.
const { dataTestid, suffix } = useDataTestid(
  computed(() => `${selectDataTestid.value}-option-${option.value.label}`),
);

// reka's SelectItem sets `aria-labelledby` to the SelectItemText id, so the option's accessible NAME
// is just the label — the description <p> is excluded and a screen reader never reads it (unlike
// reka's Combobox, whose item has no aria-labelledby so its full text content is the name). Expose
// the description as the option's accessible DESCRIPTION instead, so it is announced after the label.
// Gates both the aria-describedby wiring and the matching <p> render below, so the referenced id
// only exists when its element does. Use truthiness (not `!= null`) so an empty string — which
// renders no <p> — doesn't leave a dangling ARIA reference.
const hasDescription = computed(() => 'description' in slots || !!option.value.description);
const descriptionId = useId();

// Associate each option with its group's label (when inside a KaSelectGroup) so assistive tech
// announces the group for every item, not only when entering the group. reka's SelectLabel uses the
// group context id as its element id, so we reference it here. KaSelect uses roving DOM focus, which
// — unlike KaCombobox's aria-activedescendant — otherwise only conveys the group at its boundary.
const selectGroupContext = injectSelectGroupContext({ id: '' });

const describedBy = computed(() => {
  const ids = [selectGroupContext.id, hasDescription.value ? descriptionId : undefined].filter(
    Boolean,
  );
  return ids.length ? ids.join(' ') : undefined;
});
</script>

<template>
  <SelectItem
    :data-testid="dataTestid"
    :value="option"
    :text-value="option.textValue ?? option.label"
    :disabled="option.disabled"
    :aria-describedby="describedBy"
    :class="
      cnMerge(
        selectOptionVariants({
          withLeadingPadding:
            ('left' in slots || !!option.left) && (!!option.description || 'description' in slots),
        }),
        option.containerClasses,
      )
    "
  >
    <div class="flex w-full items-start justify-between group-data-[disabled]:opacity-50">
      <KaSelectIconSlot
        v-if="'left' in slots || option.left"
        :data-testid="suffix('left')"
        class="mr-2 inline-flex"
      >
        <slot name="left">
          <div
            :class="cnMerge('flex size-6 items-center justify-center', option.left?.wrapperClasses)"
          >
            <component
              :is="option.left?.icon"
              v-if="option.left"
              :class="cnMerge('size-3.5', option.left?.classes)"
            />
          </div>
        </slot>
      </KaSelectIconSlot>

      <div
        :class="[
          'flex-grow-1 mr-3 flex w-[calc(100%-64px)] flex-col',
          { 'mt-[1px]': 'left' in slots || option.left },
        ]"
      >
        <div :class="cnMerge('flex items-center justify-between', option.labelContainerClasses)">
          <SelectItemText
            v-if="'default' in slots || option.label"
            :class="[
              'm-0 flex-1 text-gray-900 group-data-[state=checked]:font-semibold group-data-[state=unchecked]:font-normal dark:text-gray-50',
              { truncate: option.label && !option.label.includes(' ') },
            ]"
            :title="option.label"
          >
            <slot :label="option.label">
              {{ option.label }}
            </slot>
          </SelectItemText>

          <KaSelectIconSlot
            v-if="'right' in slots || option.right"
            :data-testid="suffix('right')"
            :icon-options="option.right"
            class="inline-flex"
          >
            <slot name="right" />
          </KaSelectIconSlot>
        </div>

        <p
          v-if="hasDescription"
          :id="descriptionId"
          :class="[
            'mx-0 mb-0 mt-1 flex text-gray-500 dark:text-gray-400',
            { truncate: option.description && !option.description.includes(' ') },
          ]"
        >
          <slot name="description" v-bind="{ description: option.description }">
            {{ option.description }}
          </slot>
        </p>
      </div>

      <span class="h-5 w-5 flex-shrink-0 flex-grow-0">
        <SelectItemIndicator>
          <IconCheck
            :data-testid="suffix('check')"
            class="m-0 h-full w-full p-0 text-gray-900 dark:text-gray-50"
            aria-hidden="true"
          />
        </SelectItemIndicator>
      </span>
    </div>
  </SelectItem>
</template>
